import React from "react";
import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	Box,
	Grid,
	Typography,
	BottomNavigation,
	BottomNavigationAction,
	Fab,
	Card,
	CardContent,
	CircularProgress,
	Alert,
	Chip,
	Stack,
	Paper,
	Portal,
} from "@mui/material";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import BuildIcon from "@mui/icons-material/Build";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import { Machine } from "../graphql/querys/machine";
import ErrorScreen from "./error-screen";

export function toReadableDate(date) {
	date = new Date(date);
	let day = date.getDate();
	let month = date.getMonth() + 1;
	let year = date.getFullYear();

	return `${day}/${month}/${year}`;
}

export default function MachineScreenMobile() {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const { loading, error, data, refetch } = useQuery(Machine, {
		variables: { id },
	});

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return <ErrorScreen error={error} onRetry={() => refetch()} />;
	}

	const machine = data?.machine;
	const items = machine?.itemsInfo || [];

	return (
		<>
			<Fab
				onClick={() => {
					navigate(`/perfil-mobile`);
				}}
				sx={{
					margin: 0,
					top: 20,
					right: 20,
					position: "fixed",
				}}
				color="primary"
				aria-label={t("ACTIONS.BACK")}>
				<FirstPageIcon />
			</Fab>

			<Box
				sx={{
					width: "100%",
					p: 2,
					pt: 10,
					pb: "calc(128px + env(safe-area-inset-bottom))",
				}}
			>
				{!machine ? (
					<Alert severity="warning">{t("MOBILE_MACHINE.NOT_FOUND")}</Alert>
				) : (
					<Stack spacing={2}>
						<Card>
							<CardContent>
								<Stack spacing={1}>
									<Typography variant="h5" fontWeight={700}>
										{machine.name || t("MOBILE_MACHINE.WITHOUT_NAME")}
									</Typography>
									<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
										{machine.ref && (
											<Chip
												size="small"
												label={t("MOBILE_MACHINE.REF", { ref: machine.ref })}
											/>
										)}
										{machine.category && (
											<Chip
												size="small"
												label={t("MOBILE_MACHINE.CATEGORY", {
													category: machine.category,
												})}
											/>
										)}
										{machine.status && (
											<Chip
												size="small"
												label={t("MOBILE_MACHINE.STATUS", {
													status: t(`STATUS.${machine.status}`, machine.status),
												})}
											/>
										)}
									</Stack>
									{machine.serialNumber && (
										<Typography variant="body2" color="text.secondary">
											{t("MOBILE_MACHINE.SERIAL_NUMBER", {
												serialNumber: machine.serialNumber,
											})}
										</Typography>
									)}
									{machine.photo && (
										<Box
											component="img"
											src={machine.photo}
											alt={machine.name || t("MOBILE_MACHINE.WITHOUT_NAME")}
											sx={{
												width: "100%",
												maxHeight: 220,
												objectFit: "cover",
												borderRadius: 2,
												mt: 1,
											}}
										/>
									)}
								</Stack>
							</CardContent>
						</Card>

						<Typography variant="h6">{t("MOBILE_MACHINE.ITEMS_TITLE")}</Typography>

						{items.length === 0 ? (
							<Alert severity="info">
								{t("MOBILE_MACHINE.NO_ITEMS")}
							</Alert>
						) : (
							<Grid container direction="column" spacing={2}>
								{items.map((item, index) => (
									<Grid item key={item?._id || index}>
										<Card>
											<CardContent>
												<Typography variant="h6">
													{item?.name || t("MOBILE_MACHINE.ITEM_WITHOUT_NAME")}
												</Typography>
												{item?.brand && (
													<Typography sx={{ mb: 1 }} color="text.secondary">
														{t("FIELDS.brand")}: {item.brand}
													</Typography>
												)}
												{item?.ref && (
													<Typography variant="body2">
														{t("FIELDS.ref")}: {item.ref}
													</Typography>
												)}
											</CardContent>
										</Card>
									</Grid>
								))}
							</Grid>
						)}
					</Stack>
				)}
			</Box>

			<Portal>
				<Paper
					elevation={12}
					sx={{
						position: "fixed",
						left: 12,
						right: 12,
						bottom: "max(12px, env(safe-area-inset-bottom))",
						zIndex: 2147483647,
						borderRadius: 3,
						overflow: "hidden",
						border: "1px solid",
						borderColor: "divider",
						bgcolor: "background.paper",
						boxShadow: "0 12px 32px rgba(0,0,0,0.28)",
					}}
				>
					<BottomNavigation
						showLabels
						sx={{
							width: "100%",
							height: 72,
							"& .MuiBottomNavigationAction-root": {
								minWidth: 0,
								py: 1,
							},
							"& .MuiBottomNavigationAction-label": {
								fontSize: "0.78rem",
							},
						}}
					>
						<BottomNavigationAction
							label={t("MOBILE_MACHINE.HISTORY")}
							icon={<HistoryToggleOffIcon />}
							onClick={() => {
								navigate(`/history/${id}`);
							}}
						/>
						<BottomNavigationAction
							label={t("MOBILE_MACHINE.MAINTENANCE")}
							icon={<HomeRepairServiceIcon />}
							onClick={() => {
								navigate(`/manut/${id}`);
							}}
						/>
						<BottomNavigationAction
							label={t("MOBILE_MACHINE.REPAIR")}
							icon={<BuildIcon />}
							onClick={() => {
								navigate(`/repair/${id}`);
							}}
						/>
					</BottomNavigation>
				</Paper>
			</Portal>
		</>
	);
}
